import { useNavigate, useSearchParams } from 'react-router-dom'
import { Trash2, FileText, ArrowLeft, FlaskConical, Users, Search, Database, ShieldCheck, ExternalLink, Presentation, FileCheck, Workflow, MessageSquareText, Route, Network, FlaskRound, GraduationCap, Clock } from 'lucide-react'
import { DeleteEntityTool } from './DeleteEntityTool'
import { HelpDocBuilder } from './HelpDocBuilder'
import { PocTool } from './PocTool'
import { PipelineProductsTool } from './PipelineProductsTool'
import { MatchRateTool } from './MatchRateTool'
import { DataSourcesPage } from '@features/data-sources'
import { DemoPrepTool } from './DemoPrepTool'
import { RfpTool } from './RfpTool'
import { CascadeBuilder } from './CascadeBuilder'
import { TranscriptsTool } from './TranscriptsTool'
import { ProcessFlowTool } from './ProcessFlowTool'
import { RoadmapTool } from './RoadmapTool'
import { SandboxTool } from './SandboxTool'
import { LearningPlanTool } from './LearningPlanTool'
import { SchedulerTool } from './SchedulerTool'

const tools = [
  {
    id: 'learning-plan',
    name: 'Learning Plan',
    description: 'Personalised SME training — 355 identity topics with spaced repetition, powered by Claude voice mode.',
    icon: GraduationCap,
    iconColor: 'text-violet-400',
    iconBg: 'bg-violet-500/10',
  },
  {
    id: 'process-flow',
    name: 'Process Flow',
    description: 'Visual flowchart of how Presales and Implementations work — from deal trigger to go-live, with scope lifecycle and KPIs.',
    icon: Route,
    iconColor: 'text-pink-400',
    iconBg: 'bg-pink-500/10',
  },
  {
    id: 'roadmap',
    name: 'DealHub Roadmap',
    description: 'Product tree showing every feature in DealHub — Presales scope, SOW, dashboards, tools, and Implementations.',
    icon: Network,
    iconColor: 'text-lime-400',
    iconBg: 'bg-lime-500/10',
  },
  {
    id: 'scheduler',
    name: 'Scheduler',
    description: 'Manage automated Slack and email notifications — calendar summaries, weekly updates, and scheduled reports.',
    icon: Clock,
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/10',
  },
  {
    id: 'sandbox-seed',
    name: 'Sandbox Seeder',
    description: 'Create pre-configured test entities in the FrankieOne PreSales Sandbox — PEP, sanctions, fraud, visa checks, and clean passes.',
    icon: FlaskRound,
    iconColor: 'text-green-400',
    iconBg: 'bg-green-500/10',
  },
  {
    id: 'delete-entity',
    name: 'Entity Delete Tool',
    description: 'Bulk-delete entities from a FrankieOne environment using CSV or pasted IDs.',
    icon: Trash2,
    iconColor: 'text-red-400',
    iconBg: 'bg-red-500/10',
  },
  {
    id: 'help-doc-builder',
    name: 'Help Doc Builder',
    description: 'Generate customer-specific V1/V2 integration guides with toggleable features (KYC, IDV, KYB, AML, etc.) and PDF export.',
    icon: FileText,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-500/10',
  },
  {
    id: 'poc-tool',
    name: 'POC Manager',
    description: 'Run proof-of-concept tests: vendor selection, batch execution tracking, and pass-rate cascade analysis.',
    icon: FlaskConical,
    iconColor: 'text-cyan-400',
    iconBg: 'bg-cyan-500/10',
  },
  {
    id: 'pipeline-products',
    name: 'Pipelines and Products',
    description: 'Weekly agenda for the sales & products sync — upcoming scopes to review, custom work to discuss, and shared notes.',
    icon: Users,
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/10',
  },
  {
    id: 'match-rate',
    name: 'Match Rate Enquiry',
    description: 'AI-powered analysis of FrankieOne match rates — enter prospect requirements in natural language, get vendor recommendations per country.',
    icon: Search,
    iconColor: 'text-violet-400',
    iconBg: 'bg-violet-500/10',
  },
  {
    id: 'demo-prep',
    name: 'Demo Prep',
    description: 'AI-generated talking points for FrankieOne product demos — paste emails or notes, get a tailored demo plan.',
    icon: Presentation,
    iconColor: 'text-orange-400',
    iconBg: 'bg-orange-500/10',
  },
  {
    id: 'transcripts',
    name: 'Transcripts',
    description: 'Centralised view of call transcripts and AI-condensed HubSpot activity excerpts for any deal.',
    icon: MessageSquareText,
    iconColor: 'text-cyan-400',
    iconBg: 'bg-cyan-500/10',
  },
  {
    id: 'rfp-tool',
    name: 'RFP Tool',
    description: 'Upload RFP/RFI spreadsheets, search reference docs with AI, assess fit, and manage response assignments.',
    icon: FileCheck,
    iconColor: 'text-indigo-400',
    iconBg: 'bg-indigo-500/10',
  },
  {
    id: 'cascade-builder',
    name: 'Cascade Builder',
    description: 'Visual KYC cascade designer — map out verification flows with capture methods, validation vendors, rulesets, and decision outcomes.',
    icon: Workflow,
    iconColor: 'text-sky-400',
    iconBg: 'bg-sky-500/10',
  },
  {
    id: 'data-sources',
    name: 'Data Sources',
    description: 'Registry of source documents and spreadsheets that power DealHub tools — track versions, ownership, and quarterly refresh status.',
    icon: Database,
    iconColor: 'text-teal-400',
    iconBg: 'bg-teal-500/10',
  },
  {
    id: 'risk-profile',
    name: 'Risk Profile Documentation',
    description: 'Document and configure customer risk profiles — risk appetite, risk factors, and scoring models for implementation handover.',
    icon: ShieldCheck,
    iconColor: 'text-rose-400',
    iconBg: 'bg-rose-500/10',
    externalUrl: 'https://frankie-risk-profile.netlify.app/',
  },
] as const

export function ToolsTab({ toolId }: { toolId?: string }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Show specific tool
  if (toolId === 'learning-plan') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <LearningPlanTool />
      </div>
    )
  }

  if (toolId === 'process-flow') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <ProcessFlowTool />
      </div>
    )
  }

  if (toolId === 'roadmap') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <RoadmapTool />
      </div>
    )
  }

  if (toolId === 'scheduler') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <SchedulerTool />
      </div>
    )
  }

  if (toolId === 'sandbox-seed') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <SandboxTool />
      </div>
    )
  }

  if (toolId === 'delete-entity') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <DeleteEntityTool />
      </div>
    )
  }

  if (toolId === 'help-doc-builder') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <HelpDocBuilder />
      </div>
    )
  }

  if (toolId === 'poc-tool') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <PocTool />
      </div>
    )
  }

  if (toolId === 'pipeline-products') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <PipelineProductsTool />
      </div>
    )
  }

  if (toolId === 'match-rate') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <MatchRateTool />
      </div>
    )
  }

  if (toolId === 'transcripts') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <TranscriptsTool />
      </div>
    )
  }

  if (toolId === 'demo-prep') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <DemoPrepTool />
      </div>
    )
  }

  if (toolId === 'rfp-tool') {
    return <RfpTool />
  }

  if (toolId === 'cascade-builder') {
    const fromBlueprint = searchParams.get('from') === 'blueprint'
    const sessionId = searchParams.get('session')
    return (
      <div className="space-y-4">
        <button
          onClick={() => fromBlueprint && sessionId
            ? navigate(`/scope?session=${sessionId}&tab=blueprint`)
            : navigate('/dashboard/presales/tools')
          }
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          {fromBlueprint ? 'Back to Blueprint' : 'Back to Tools'}
        </button>
        <CascadeBuilder />
      </div>
    )
  }

  if (toolId === 'data-sources') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/presales/tools')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Tools
        </button>
        <DataSourcesPage />
      </div>
    )
  }

  // Tools index
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Presales Tools</h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Quick-access utilities for the presales team
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {tools.map((tool) => {
          const Icon = tool.icon
          const isExternal = 'externalUrl' in tool && tool.externalUrl

          if (isExternal) {
            return (
              <a
                key={tool.id}
                href={tool.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-[var(--accent-color)] hover:bg-[var(--bg-tertiary)] transition-all text-left group"
              >
                <div className={`p-2.5 rounded-lg ${tool.iconBg} shrink-0`}>
                  <Icon size={20} className={tool.iconColor} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent-color)] transition-colors flex items-center gap-1.5">
                    {tool.name}
                    <ExternalLink size={12} className="text-[var(--text-muted)] shrink-0" />
                  </h3>
                  <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">
                    {tool.description}
                  </p>
                </div>
              </a>
            )
          }

          return (
            <button
              key={tool.id}
              onClick={() => {
                navigate(`/dashboard/presales/tools/${tool.id}`)
              }}
              className="flex items-start gap-4 p-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-[var(--accent-color)] hover:bg-[var(--bg-tertiary)] transition-all text-left group"
            >
              <div className={`p-2.5 rounded-lg ${tool.iconBg} shrink-0`}>
                <Icon size={20} className={tool.iconColor} />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent-color)] transition-colors">
                  {tool.name}
                </h3>
                <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">
                  {tool.description}
                </p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
